/**
 * Email Editor Component using Editor.js
 * Clean, block-based email composition
 */

import React, { useEffect, useRef, useCallback } from 'react';
import EditorJS, { OutputData } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import Paragraph from '@editorjs/paragraph';
import List from '@editorjs/list';
import Delimiter from '@editorjs/delimiter';

import { ButtonBlock, HeroBlock, ProductBlock, PromotionBlock, FooterBlock, ImageBlock, MergeTagBlock } from './blocks';
import { convertToEmailHTML, extractPlainText } from './htmlConverter';
import './EmailEditorJS.css';

interface EmailEditorJSProps {
  initialData?: OutputData;
  onChange?: (data: OutputData, html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

const EmailEditorJS: React.FC<EmailEditorJSProps> = ({
  initialData,
  onChange,
  placeholder = 'Start composing your email...',
  readOnly = false,
}) => {
  const editorRef = useRef<EditorJS | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isReady = useRef(false);

  // Initialize Editor.js
  useEffect(() => {
    if (!containerRef.current || editorRef.current) return;

    const editor = new EditorJS({
      holder: containerRef.current,
      placeholder,
      readOnly,
      autofocus: true,
      inlineToolbar: ['bold', 'italic', 'link'],

      tools: {
        // Built-in blocks
        header: {
          class: Header as any,
          config: {
            placeholder: 'Enter a heading',
            levels: [1, 2, 3, 4],
            defaultLevel: 2,
          },
          inlineToolbar: true,
        },
        paragraph: {
          class: Paragraph as any,
          inlineToolbar: true,
        },
        list: {
          class: List as any,
          inlineToolbar: true,
          config: {
            defaultStyle: 'unordered',
          },
        },
        delimiter: Delimiter,

        // Custom email blocks
        button: {
          class: ButtonBlock as any,
        },
        hero: {
          class: HeroBlock as any,
        },
        product: {
          class: ProductBlock as any,
        },
        promotion: {
          class: PromotionBlock as any,
        },
        footer: {
          class: FooterBlock as any,
        },
        emailImage: {
          class: ImageBlock as any,
        },
        mergeTag: {
          class: MergeTagBlock as any,
        },
      },

      // Only use initialData if it has valid blocks
      data: initialData && initialData.blocks && initialData.blocks.length > 0
        ? initialData
        : { blocks: [] },

      onReady: () => {
        isReady.current = true;
      },

      onChange: async (api) => {
        if (!isReady.current || !onChange) return;

        try {
          const data = await api.saver.save();
          const html = convertToEmailHTML(data);
          onChange(data, html);
        } catch (error) {
          console.error('Error saving editor data:', error);
        }
      },
    });

    editorRef.current = editor;

    return () => {
      if (editorRef.current && isReady.current) {
        editorRef.current.destroy();
        editorRef.current = null;
        isReady.current = false;
      }
    };
  }, []);

  // Method to get current data
  const getData = useCallback(async (): Promise<{ data: OutputData; html: string }> => {
    if (!editorRef.current || !isReady.current) {
      return { data: { blocks: [] }, html: '' };
    }

    const data = await editorRef.current.save();
    const html = convertToEmailHTML(data);
    return { data, html };
  }, []);

  // Method to clear editor
  const clear = useCallback(async () => {
    if (editorRef.current && isReady.current) {
      await editorRef.current.clear();
    }
  }, []);

  // Expose methods via ref
  React.useImperativeHandle(
    React.useRef({ getData, clear }),
    () => ({ getData, clear }),
    [getData, clear]
  );

  return (
    <div className="email-editor-js">
      <div className="email-editor-js__toolbar">
        <div className="email-editor-js__toolbar-label">
          <span>Email Content</span>
        </div>
        <div className="email-editor-js__toolbar-hint">
          Press <kbd>Tab</kbd> or <kbd>/</kbd> to add blocks
        </div>
      </div>

      <div className="email-editor-js__content">
        <div ref={containerRef} className="email-editor-js__editorjs" />
      </div>

      <div className="email-editor-js__blocks-guide">
        <h4>Available Blocks</h4>
        <div className="email-editor-js__blocks-list">
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">H</span>
            <span>Heading</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">¶</span>
            <span>Paragraph</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">•</span>
            <span>List</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">—</span>
            <span>Divider</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">▢</span>
            <span>Button</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">☰</span>
            <span>Hero</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">★</span>
            <span>Promotion</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">🍷</span>
            <span>Product</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">📷</span>
            <span>Image</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">⌂</span>
            <span>Footer</span>
          </div>
          <div className="email-editor-js__block-item">
            <span className="email-editor-js__block-icon">{'{}'}</span>
            <span>Merge Tag</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailEditorJS;

// Export helper functions
export { convertToEmailHTML, extractPlainText };
